import { organizationalBoundaryIssues, type OrgBoundaryDraft } from '../components/ghg/OrganizationalBoundaryEase';
import { energyNeedsDiscom } from '../constants/msmeSectorFormOptions';

export type MyCompanyPageId = 'company' | 'energy' | 'units' | 'scope1' | 'scope3' | 'boundaries';

export type MyCompanyPageCheck = {
  id: MyCompanyPageId;
  label: string;
  path: string;
  complete: boolean;
  missing: string[];
};

const PLACEHOLDER_UNIT = /^(unit\s*1|main plant|plant 1|site 1)$/i;

type ProfileShape = {
  companyName?: string;
  gstNumber?: string;
  manufacturingProfile?: {
    exportActivity?: boolean;
    primaryEnergySource?: string;
    electricityDiscomId?: string;
    mainFuelsUsed?: string[];
    ghgOrganizationalBoundary?: Record<string, unknown>;
  };
  business?: {
    manufacturingWorkflow?: {
      units?: Array<{ name?: string; products?: string[] | string }>;
    };
  };
  operations?: {
    ghgOperationalBoundary?: {
      scope1StationaryCombustion?: boolean;
      scope1MobileCombustion?: boolean;
      scope1ProcessEmissions?: boolean;
      scope1FugitiveEmissions?: boolean;
      noDirectEmissionSources?: boolean;
      directEmissionsReviewedAt?: string | Date | null;
      scope3CategoriesIncluded?: Array<number | string>;
      scope3OutOfBoundarySummary?: string;
      valueChainReviewedAt?: string | Date | null;
    };
  };
};

const text = (value: unknown) => String(value ?? '').trim();

const orgDraftFromProfile = (profile: ProfileShape): OrgBoundaryDraft => {
  const ob = (profile.manufacturingProfile?.ghgOrganizationalBoundary || {}) as Record<string, unknown>;
  const holding = (ob.holdingCompany || {}) as Record<string, unknown>;
  const party = (row: Record<string, unknown>) => ({
    name: text(row.name),
    ownershipPercent: row.ownershipPercent != null ? String(row.ownershipPercent) : '',
    operationalControl: row.operationalControl !== false,
  });
  return {
    consolidationApproach: text(ob.consolidationApproach),
    reportingEntityDescription: text(ob.reportingEntityDescription),
    includedLegalEntities: Array.isArray(ob.includedLegalEntities)
      ? ob.includedLegalEntities.map((row) => {
        const entity = (row || {}) as Record<string, unknown>;
        return {
          name: text(entity.name),
          relationshipType: text(entity.relationshipType),
          consolidationBasis: text(entity.consolidationBasis),
        };
      })
      : [],
    jointVentureEmissionAllocation: text(ob.jointVentureEmissionAllocation),
    franchisesOrOutsourcedOperationsTreatment: text(ob.franchisesOrOutsourcedOperationsTreatment),
    nonControlledOperationsExcluded: ob.nonControlledOperationsExcluded !== false,
    organizationalBoundaryNotes: text(ob.organizationalBoundaryNotes),
    onSiteFuelCombustion: text(ob.onSiteFuelCombustion) || 'unknown',
    fleetControl: text(ob.fleetControl) || 'unknown',
    logisticsArrangement: text(ob.logisticsArrangement) || 'unknown',
    reportingEquitySharePercent: ob.reportingEquitySharePercent != null
      ? String(ob.reportingEquitySharePercent)
      : '',
    singleLegalEntity: Boolean(ob.singleLegalEntity),
    holdingCompany: {
      name: text(holding.name),
      ownershipPercent: holding.ownershipPercent != null ? String(holding.ownershipPercent) : '',
      country: text(holding.country) || 'India',
      includedInBoundary: holding.includedInBoundary !== false && Boolean(text(holding.name)),
    },
    subsidiaries: Array.isArray(ob.subsidiaries)
      ? ob.subsidiaries.map((row) => party((row || {}) as Record<string, unknown>))
      : [],
    jointVentures: Array.isArray(ob.jointVentures)
      ? ob.jointVentures.map((row) => party((row || {}) as Record<string, unknown>))
      : [],
  };
};

const namedUnits = (profile: ProfileShape) => {
  const units = profile.business?.manufacturingWorkflow?.units;
  if (!Array.isArray(units)) return [];
  return units.filter((unit) => {
    const name = text(unit?.name);
    return Boolean(name) && !PLACEHOLDER_UNIT.test(name);
  });
};

const unitHasProducts = (unit: { products?: string[] | string }) => {
  if (Array.isArray(unit.products)) return unit.products.some((item) => text(item));
  return Boolean(text(unit.products));
};

export function myCompanyPageChecks(profile: ProfileShape | null | undefined): MyCompanyPageCheck[] {
  const source = profile || {};
  const manufacturing = source.manufacturingProfile || {};
  const operational = source.operations?.ghgOperationalBoundary || {};

  const companyMissing: string[] = [];
  if (!text(source.companyName)) companyMissing.push('Company name');
  if (!text(source.gstNumber)) companyMissing.push('GST number');
  if (typeof manufacturing.exportActivity !== 'boolean') companyMissing.push('Export activity');

  const energyMissing: string[] = [];
  if (!text(manufacturing.primaryEnergySource)) energyMissing.push('Primary energy source');
  const fuels = Array.isArray(manufacturing.mainFuelsUsed) ? manufacturing.mainFuelsUsed : [];
  if (!fuels.some((fuel) => text(fuel))) energyMissing.push('Main fuels used');
  if (energyNeedsDiscom(text(manufacturing.primaryEnergySource)) && !text(manufacturing.electricityDiscomId)) {
    energyMissing.push('Electricity supplier');
  }

  const units = namedUnits(source);
  const unitsMissing: string[] = [];
  if (units.length === 0) unitsMissing.push('Manufacturing unit name');
  if (units.length === 0 || units.some((unit) => !unitHasProducts(unit))) {
    unitsMissing.push('Products manufactured');
  }

  const scope1Chosen = Boolean(
    operational.scope1StationaryCombustion
    || operational.scope1MobileCombustion
    || operational.scope1ProcessEmissions
    || operational.scope1FugitiveEmissions
    || operational.noDirectEmissionSources
  );
  const scope1Missing: string[] = [];
  if (!operational.directEmissionsReviewedAt) scope1Missing.push('Save direct emissions');
  if (!scope1Chosen) scope1Missing.push('Direct emission sources');

  const categories = Array.isArray(operational.scope3CategoriesIncluded)
    ? operational.scope3CategoriesIncluded.filter((item) => Number(item) >= 1)
    : [];
  const outside = text(operational.scope3OutOfBoundarySummary);
  const scope3Missing: string[] = [];
  if (categories.length === 0) scope3Missing.push('Value-chain categories');
  if (outside.length < 3) scope3Missing.push('What stays outside the value chain');
  if (!operational.valueChainReviewedAt) scope3Missing.push('Save value chain');

  const boundaryIssues = organizationalBoundaryIssues(orgDraftFromProfile(source));

  return [
    {
      id: 'company',
      label: 'Company profile',
      path: '/msme-profile',
      complete: companyMissing.length === 0,
      missing: companyMissing,
    },
    {
      id: 'energy',
      label: 'Energy and meters',
      path: '/operations',
      complete: energyMissing.length === 0,
      missing: energyMissing,
    },
    {
      id: 'units',
      label: 'Manufacturing units',
      path: '/operations/units',
      complete: unitsMissing.length === 0,
      missing: unitsMissing,
    },
    {
      id: 'scope1',
      label: 'Direct emissions',
      path: '/operations/scope-1',
      complete: scope1Missing.length === 0,
      missing: scope1Missing,
    },
    {
      id: 'scope3',
      label: 'Value chain',
      path: '/operations/scope-3',
      complete: scope3Missing.length === 0,
      missing: scope3Missing,
    },
    {
      id: 'boundaries',
      label: 'Boundaries',
      path: '/msme-profile/boundaries',
      complete: boundaryIssues.length === 0,
      missing: boundaryIssues,
    },
  ];
}

export function firstIncompleteMyCompanyPage(profile: ProfileShape | null | undefined): MyCompanyPageCheck | null {
  return myCompanyPageChecks(profile).find((page) => !page.complete) ?? null;
}

export function myCompanyPagesComplete(profile: ProfileShape | null | undefined): boolean {
  return firstIncompleteMyCompanyPage(profile) == null;
}

/** Progress label for Home/checklist UI. Complete still means myCompanyPageChecks.complete. */
export type MyCompanyProgressStatus = 'not_started' | 'in_progress' | 'complete';

export function myCompanyPageProgressStatus(
  profile: ProfileShape | null | undefined,
  pageId: MyCompanyPageId
): MyCompanyProgressStatus {
  const check = myCompanyPageChecks(profile).find((page) => page.id === pageId);
  if (!check) return 'not_started';
  if (check.complete) return 'complete';

  const source = profile || {};
  const manufacturing = source.manufacturingProfile || {};
  const operational = source.operations?.ghgOperationalBoundary || {};

  const hasSignal = (() => {
    switch (pageId) {
      case 'company':
        return Boolean(
          text(source.companyName)
          || text(source.gstNumber)
          || typeof manufacturing.exportActivity === 'boolean'
        );
      case 'energy':
        return Boolean(
          text(manufacturing.primaryEnergySource)
          || (Array.isArray(manufacturing.mainFuelsUsed) && manufacturing.mainFuelsUsed.some((fuel) => text(fuel)))
          || text(manufacturing.electricityDiscomId)
        );
      case 'units':
        return namedUnits(source).length > 0
          || Boolean(source.business?.manufacturingWorkflow?.units?.some((unit) => text(unit?.name)));
      case 'scope1':
        return Boolean(
          operational.directEmissionsReviewedAt
          || operational.scope1StationaryCombustion
          || operational.scope1MobileCombustion
          || operational.scope1ProcessEmissions
          || operational.scope1FugitiveEmissions
          || operational.noDirectEmissionSources
        );
      case 'scope3': {
        const categories = Array.isArray(operational.scope3CategoriesIncluded)
          ? operational.scope3CategoriesIncluded.filter((item) => Number(item) >= 1)
          : [];
        return Boolean(
          operational.valueChainReviewedAt
          || categories.length > 0
          || text(operational.scope3OutOfBoundarySummary)
        );
      }
      case 'boundaries': {
        const ob = manufacturing.ghgOrganizationalBoundary || {};
        return Boolean(
          text(ob.consolidationApproach)
          || text(ob.reportingEntityDescription)
          || (text(ob.onSiteFuelCombustion) && text(ob.onSiteFuelCombustion) !== 'unknown')
          || (text(ob.fleetControl) && text(ob.fleetControl) !== 'unknown')
          || (text(ob.logisticsArrangement) && text(ob.logisticsArrangement) !== 'unknown')
        );
      }
      default:
        return false;
    }
  })();

  return hasSignal ? 'in_progress' : 'not_started';
}

/** Routes that stay open while My company pages are still incomplete. */
export function myCompanyPathAllowedWhileIncomplete(pathname: string): boolean {
  if (pathname === '/dashboard' || pathname.startsWith('/dashboard/')) return true;
  if (pathname === '/msme-profile' || pathname.startsWith('/msme-profile/')) return true;
  if (pathname === '/operations' || pathname.startsWith('/operations/')) return true;
  if (pathname === '/settings' || pathname.startsWith('/settings/')) return true;
  if (pathname === '/data-privacy' || pathname === '/information' || pathname === '/help') return true;
  if (pathname === '/compliance/geotagged-capture') return true;
  return false;
}

/** Keep Home and header links on the unfinished My company page instead of a locked route. */
export function continuePathWhileMyCompanyOpen(
  target: string,
  profile: ProfileShape | null | undefined
): string {
  const next = firstIncompleteMyCompanyPage(profile);
  if (!next) return target;
  const pathOnly = target.split('?')[0];
  if (myCompanyPathAllowedWhileIncomplete(pathOnly)) return target;
  return next.path;
}
